import React, { useRef } from "react";
import { Bubble } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable12({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  // Bubble styling rotation
  const colors = [
    "#F6CFFF",
    "#FFF085",
    "#82181A",
    "#9F9FA9",
    "#E9D4FF",
    "#D8F999",
    "#2C92B8",
  ];

  // Prepare bubble dataset
  const bubbleData = data.map((item, index) => ({
    x: item.year,
    y: Number(item.percentage),
    r: Math.sqrt(Number(item.all_education_categories)) / 14,
    backgroundColor: colors[index % colors.length],
    borderColor: colors[index % colors.length],
  }));

  const chartData = {
    datasets: bubbleData.map((obj, i) => ({
      label: `${obj.x}`,
      data: [obj],
      backgroundColor: obj.backgroundColor,
      borderColor: obj.borderColor,
      borderWidth: 2,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "% Employment with Bachelor's or Higher Degree (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        min: 2017,
        title: { display: true, text: "Year", font: { size: 14, weight: "bold" } },
        ticks: {
          stepSize: 1,
          callback: (value) => value.toString(),
        },
      },
      y: {
        min: 0,
        max: 40,
        title: { display: true, text: "Percentage (%)", font: { size: 14, weight: "bold" } },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  // Summary Metrics
  const first = Number(data[0].all_education_categories);
  const last = Number(data[data.length - 1].all_education_categories);
  const percentGrowth = (((last - first) / first) * 100).toFixed(1);

  const peakYear = data.reduce(
    (max, item) =>
      Number(item.all_education_categories) > max.total
        ? { year: item.year, total: item.all_education_categories }
        : max,
    { year: data[0].year, total: first }
  );

  // PDF Export
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right"
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        pdf.line(10, 260, 200, 260);
        const footerY = 265;
        pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
        pdf.setFontSize(10);
        pdf.text("Built by", 55, footerY + 10);
        pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
        pdf.text("using", 112, footerY + 10);
        pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
        pdf.text("20250916.v3.198", 148, footerY + 10);
        pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
        pdf.text(`From: ${window.location.href}`, 10, 295);

        pdf.save("cs_it_bachelor_emp_trend.pdf");
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-lg border border-gray-200"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment with Bachelor's or Higher Degree 🎓 (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Trend Growth</p>
            <p className="text-xl font-bold text-blue-700">+{percentGrowth}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Peak Year</p>
            <p className="text-xl font-bold text-yellow-700">{peakYear.year}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">2024 Employment Level</p>
            <p className="text-xl font-bold text-green-700">
              {last.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Total Employment</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>% Bachelor's+</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.all_education_categories).toLocaleString(
                          "en-US"
                        )}
                      </td>
                      <td
                        className="p-2 font-bold"
                        style={{
                          color: row.percentage >= 30 ? "#0ea5e9" : "#ef4444",
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        {row.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bubble Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bubble data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg"
        >
          Download as PDF
          <img src={pdfIcon} alt="pdf-icon" className="w-6" />
        </button>
      </div>
    </>
  );
}
